// src/hooks/index.js
import { useState, useEffect, useCallback, useRef } from "react";
import { uid } from "../utils/helpers";

// ══════════════════════════════════════════════════════════════
// useToast
// ══════════════════════════════════════════════════════════════
export function useToast() {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((msg, type = "info", duration = 3000) => {
    const id = uid();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), duration);
  }, []);

  return { toasts, toast };
}

// ══════════════════════════════════════════════════════════════
// useTimer — reads timerEndsAt from Firebase state, ticks locally
// ══════════════════════════════════════════════════════════════
export function useTimer(timerEndsAt, timerDuration) {
  const [remaining, setRemaining] = useState(0);
  const [pct, setPct]             = useState(100);

  useEffect(() => {
    if (!timerEndsAt || !timerDuration) {
      setRemaining(0);
      setPct(0);
      return;
    }

    const tick = () => {
      const r = Math.max(0, Math.ceil((timerEndsAt - Date.now()) / 1000));
      setRemaining(r);
      setPct((r / timerDuration) * 100);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [timerEndsAt, timerDuration]);

  const urgent  = remaining > 0 && remaining <= 30;
  const active  = remaining > 0 && timerEndsAt > Date.now();
  const display =
    remaining >= 60
      ? `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, "0")}`
      : `${remaining}s`;

  return { remaining, pct, urgent, active, display };
}

// ══════════════════════════════════════════════════════════════
// useLocalMe — persist ME to sessionStorage
// ══════════════════════════════════════════════════════════════
const SESSION_KEY = "omerta_me";

export function useLocalMe() {
  const load = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  const save = useCallback((me) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(me));
  }, []);

  const clear = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  return { load, save, clear };
}

// ══════════════════════════════════════════════════════════════
// useCountdown — simple n-second countdown with callback
// ══════════════════════════════════════════════════════════════
export function useCountdown(seconds, active, onComplete) {
  const [left, setLeft] = useState(seconds);
  const cbRef           = useRef(onComplete);
  cbRef.current         = onComplete;

  useEffect(() => {
    if (!active) { setLeft(seconds); return; }
    setLeft(seconds);
    const iv = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) { clearInterval(iv); cbRef.current(); return 0; }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [active, seconds]);

  return left;
}
